import type { Scenario } from './matineeApi.ts';

export type LigneHoraire = {
  libelle: string;
  dureeMin: number;
  // Heure de fin de l'etape, en minutes depuis minuit (peut etre negative ou
  // depasser 24 h : cf formaterHeure).
  finMin: number;
  type: Scenario['etapes'][number]['type'];
};

export type Horaire = {
  // Heure de lever (debut de la premiere etape), en minutes depuis minuit.
  leverMin: number;
  lignes: LigneHoraire[];
  // Fin de la derniere etape (arrivee au travail en general).
  arriveeMin: number;
  // Duree retenue pour l'etape Seance (seance + marge - trajets courus).
  seanceMin: number;
};

/**
 * Heure de lever du scenario favori pour une seance de dureeSeanceMin
 * minutes ; null sans favori ou si l'heure de fin de douche est invalide.
 */
export function leverFavori(config: { finDouche: string; scenarios: Scenario[] }, dureeSeanceMin: number):
    { scenario: Scenario; leverMin: number } | null {
  const scenario = config.scenarios.find((s) => s.favori);
  const finDouche = versMinutes(config.finDouche);
  if (!scenario || finDouche === null) return null;
  return { scenario, leverMin: calculerHoraire(scenario, finDouche, dureeSeanceMin).leverMin };
}

/** "07:50" -> 470 ; null si invalide. */
export function versMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** 470 -> "07:50" (modulo 24 h : un lever la veille reste lisible). */
export function formaterHeure(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** 65 -> "01:05". */
export function formaterDureeHm(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/**
 * Horaire d'un scenario, a rebours depuis la fin de la douche : chaque etape
 * avant la douche se termine quand commence la suivante, chaque etape apres
 * s'enchaine. La seance dure seanceMin + marge, moins les trajets courus
 * (aller au gym en courant : ils font partie de l'entrainement).
 */
export function calculerHoraire(scenario: Scenario, finDoucheMin: number, dureeSeanceMin: number): Horaire {
  const courus = scenario.etapes.filter((e) => e.type === 'COURUE').reduce((s, e) => s + e.dureeMin, 0);
  const seanceMin = Math.max(0, dureeSeanceMin + scenario.margeMin - courus);
  const durees = scenario.etapes.map((e) => (e.type === 'SEANCE' ? seanceMin : e.dureeMin));
  const iDouche = scenario.etapes.findIndex((e) => e.type === 'DOUCHE');
  // Sans douche (configuration incomplete) : la derniere etape sert de reference.
  const reference = iDouche >= 0 ? iDouche : scenario.etapes.length - 1;

  const fins: number[] = new Array(durees.length);
  fins[reference] = finDoucheMin;
  for (let i = reference - 1; i >= 0; i--) fins[i] = fins[i + 1] - durees[i + 1];
  for (let i = reference + 1; i < durees.length; i++) fins[i] = fins[i - 1] + durees[i];

  const leverMin = durees.length ? fins[0] - durees[0] : finDoucheMin;
  return {
    leverMin,
    lignes: scenario.etapes.map((e, i) => ({ libelle: e.libelle, dureeMin: durees[i], finMin: fins[i], type: e.type })),
    arriveeMin: durees.length ? fins[durees.length - 1] : finDoucheMin,
    seanceMin,
  };
}

// Cle d'alignement d'une etape : la seance et la douche s'alignent toujours
// entre elles, les autres etapes par libelle (sans casse ni accents).
function cleAlignement(e: Scenario['etapes'][number]): string {
  if (e.type === 'SEANCE') return '§seance';
  if (e.type === 'DOUCHE') return '§douche';
  return e.libelle.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/**
 * Lignes communes aux tableaux des scenarios, comme dans la feuille : les
 * etapes de meme libelle (et toujours la seance et la douche) sont sur la
 * meme ligne. Chaque scenario est fusionne dans la grille par plus longue
 * sous-suite commune ; entre deux lignes communes, ses autres etapes
 * occupent les lignes libres avant d'en ajouter.
 *
 * @return pour chaque scenario, l'index de son etape sur chaque ligne (null : ligne vide)
 */
export function alignerEtapes(scenarios: Scenario[]): (number | null)[][] {
  type Ligne = { cles: Set<string>; etapes: Map<number, number> };
  let lignes: Ligne[] = [];

  scenarios.forEach((scenario, s) => {
    const cles = scenario.etapes.map(cleAlignement);
    const m = lignes.length;
    const n = cles.length;
    const poids = (c: string) => (c.startsWith('§') ? 100 : 1);
    // poidsMax[i][j] : meilleure correspondance entre lignes[i..] et cles[j..].
    const poidsMax: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
      for (let j = n - 1; j >= 0; j--) {
        const accord = lignes[i].cles.has(cles[j]) ? poids(cles[j]) + poidsMax[i + 1][j + 1] : -1;
        poidsMax[i][j] = Math.max(accord, poidsMax[i + 1][j], poidsMax[i][j + 1]);
      }
    }
    const paires: [number, number][] = [];
    for (let i = 0, j = 0; i < m && j < n;) {
      if (lignes[i].cles.has(cles[j]) && poidsMax[i][j] === poids(cles[j]) + poidsMax[i + 1][j + 1]) {
        paires.push([i, j]);
        i++;
        j++;
      } else if (poidsMax[i][j] === poidsMax[i + 1][j]) {
        i++;
      } else {
        j++;
      }
    }
    paires.push([m, n]);

    const fusion: Ligne[] = [];
    let i0 = 0;
    let j0 = 0;
    for (const [i, j] of paires) {
      const libres = lignes.slice(i0, i);
      fusion.push(...libres);
      for (let k = 0; k < j - j0; k++) {
        const etape = j0 + k;
        if (k < libres.length) {
          libres[k].cles.add(cles[etape]);
          libres[k].etapes.set(s, etape);
        } else {
          fusion.push({ cles: new Set([cles[etape]]), etapes: new Map([[s, etape]]) });
        }
      }
      if (i < m) {
        lignes[i].etapes.set(s, j);
        fusion.push(lignes[i]);
      }
      i0 = i + 1;
      j0 = j + 1;
    }
    lignes = fusion;
  });

  return scenarios.map((_, s) => lignes.map((l) => l.etapes.get(s) ?? null));
}
